import { useState, useEffect, useRef, Suspense } from 'react';
import { lazyModule } from './lib/lazyModule';
import { useCopy } from './i18n';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import { UIStateProvider, useUIState } from './contexts/UIStateContext';
import { UndoRedoProvider } from './contexts/UndoRedoContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { ProjectFolderProvider } from './contexts/ProjectFolderContext';
import LoginScreen from './components/auth/LoginScreen';
import ResetPasswordScreen from './components/auth/ResetPasswordScreen';
import { isAppPath, MODULOS_DE_CELULAR } from './lib/appRoute';
import Sidebar from './components/sidebar/Sidebar';
import ProjectSettingsModal from './components/modals/ProjectSettingsModal';
import SharedProjectView from './components/shared/SharedProjectView';
import SearchModal from './components/search/SearchModal';
import TrialExpiredModal from './components/billing/TrialExpiredModal';
import { showToast } from './components/common/Toast';
import { FolderKanban, Plus, Menu } from 'lucide-react';
import { Button } from './components/ui/button';
import ModuleTabBar from './components/navigation/ModuleTabBar';
import { useGlobalKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useAppRouteSync } from './hooks/useAppRouteSync';
import { useIsMobile } from './hooks/useIsMobile';
import { useVirtualKeyboard } from './hooks/useVirtualKeyboard';
import DesktopOnlyModule from './components/common/DesktopOnlyModule';
import PwaPrompts from './components/common/PwaPrompts';
import MobileTabBar from './components/mobile/MobileTabBar';
import ProjetosScreen from './components/mobile/ProjetosScreen';
import AvisosScreen from './components/mobile/AvisosScreen';
import AjustesScreen from './components/mobile/AjustesScreen';
import analytics from './services/analyticsService';

// Módulos do produto, todos em chunk próprio.
//
// Eram import estático até aqui, e por isso os quinze entravam no bundle de
// entrada: 592 KB comprimidos que toda sessão baixava antes do primeiro pixel,
// para renderizar UM módulo. Em 4G isso é a diferença entre abrir e desistir, e
// 90% das sessões do produto são de celular.
//
// Nenhum deles é a primeira tela de ninguém: o app só decide qual renderizar
// depois de resolver autenticação, assinatura e projeto corrente. O Suspense que
// cobre `renderModule` já existia (o Wiki era lazy desde antes) e não muda.
const GanttModule = lazyModule(() => import('./components/modules/GanttModule/GanttModule'));
const ScheduleModule = lazyModule(() => import('./components/modules/ScheduleModule/ScheduleModule'));
const CalendarModule = lazyModule(() => import('./components/modules/CalendarModule/CalendarModule'));
const TodosModule = lazyModule(() => import('./components/modules/TodosModule/TodosModule'));
const RisksModule = lazyModule(() => import('./components/modules/RisksModule/RisksModule'));
const ChangesModule = lazyModule(() => import('./components/modules/ChangesModule/ChangesModule'));
const CostsModule = lazyModule(() => import('./components/modules/CostsModule/CostsModule'));
const RaciModule = lazyModule(() => import('./components/modules/RaciModule/RaciModule'));
const EapModule = lazyModule(() => import('./components/modules/EapModule/EapModule'));
const KanbanModule = lazyModule(() => import('./components/modules/KanbanModule/KanbanModule'));
const TeamAllocationModule = lazyModule(() => import('./components/modules/TeamModule/TeamAllocationModule'));
const HomeModule = lazyModule(() => import('./components/modules/HomeModule/HomeModule'));
const SettingsModule = lazyModule(() => import('./components/modules/SettingsModule/SettingsModule'));
const PortfolioModule = lazyModule(() => import('./components/modules/PortfolioModule/PortfolioModule'));
// AllocationModule absorvido pelo TeamModule
const MyTasksModule = lazyModule(() => import('./components/modules/MyTasksModule/MyTasksModule'));
const HojeModule = lazyModule(() => import('./components/modules/HojeModule/HojeModule'));

// Feature gating
import FeatureGate from './components/billing/FeatureGate';
import ModuleErrorBoundary from './components/common/ModuleErrorBoundary';
import { LocaleProvider, useLocale } from './i18n';
import { splitLocale } from './i18n/locale';

// Lazy load do AdminApp, InviteAcceptPage, OrgInviteAcceptPage, ProjectInviteAcceptPage e LandingPage (chunks separados)
const AdminApp = lazyModule(() => import('./AdminApp'));
const InviteAcceptPage = lazyModule(() => import('./components/auth/InviteAcceptPage'));
const OrgInviteAcceptPage = lazyModule(() => import('./components/organization/OrgInviteAcceptPage'));
const ProjectInviteAcceptPage = lazyModule(() => import('./components/auth/ProjectInviteAcceptPage'));
const LandingPage = lazyModule(() => import('./components/landing/LandingPage'));

// A Central de Ajuda também é lazy: são 36 artigos que só quem clica em "Ajuda"
// precisa baixar, e o texto deles ainda vem por idioma dentro do módulo.
const WikiModule = lazyModule(() => import('./components/modules/WikiModule/WikiModule'));

// Idioma da landing vem do prefixo da URL (/en, /es). Ver src/i18n/locale.ts.

const OAuthConsentPage = lazyModule(() => import('./components/oauth/OAuthConsentPage'));

import CookieConsent from './components/common/CookieConsent';
import OnboardingTour from './components/common/OnboardingTour';
import WelcomeModal from './components/common/WelcomeModal';
import CompleteWhatsappModal from './components/auth/CompleteWhatsappModal';
import GettingStartedChecklist from './components/common/GettingStartedChecklist';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import AIProjectModal from './components/modals/AIProjectModal';
import PricingPage from './components/billing/PricingPage';
import KeyboardShortcutsModal from './components/common/KeyboardShortcutsModal';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';


// Loading screen para Suspense (enquanto carrega chunks lazy)
const LazyLoadingScreen = () => (
  <div className="w-screen h-screen max-md:h-[calc(100dvh_-_var(--altura-teclado,0px))] flex overflow-hidden bg-[var(--color-bg)]">
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center justify-center w-full h-full text-[var(--color-text-muted)]">
        <div className="w-10 h-10 border-3 border-[var(--color-border)] border-t-[var(--color-primary)] rounded-full animate-spin"></div>
      </div>
    </div>
  </div>
);

// Fallback de troca de módulo. Ocupa a área do módulo, não a viewport: o
// `LazyLoadingScreen` é `w-screen h-screen` porque cobre a tela inteira antes do
// shell existir, e dentro do <main> ele passaria por baixo da sidebar.
const ModuleLoadingScreen = () => (
  <div className="flex-1 flex items-center justify-center text-[var(--color-text-muted)]">
    <div className="w-10 h-10 border-3 border-[var(--color-border)] border-t-[var(--color-primary)] rounded-full animate-spin"></div>
  </div>
);

const AuthenticatedApp = () => {
  const tShell = useCopy().common.appShell;
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Ponte temporária: 'projetos' abre a gaveta atual (que já é a lista) e
  // 'mais' cai em Configurações. As telas próprias são o próximo passo, e
  // são elas que resolvem a duplicata de projeto (favorito + pasta + org).
  const [telaDoShell, setTelaDoShell] = useState<string | null>(null);
  const { loading, project, projects, error } = useProject();
  const { user } = useAuth();
  const { currentModule, changeModule } = useUIState();
  const { refreshSubscription, subscription } = useSubscription();
  const [checkoutJustSucceeded, setCheckoutJustSucceeded] = useState(false);

  // Antes dos efeitos de checkout e de ?upgrade=1, de propósito: os dois limpam
  // a query lendo `window.location.pathname`, e precisam ler o caminho já
  // normalizado para /p/<id>/<módulo>.
  useAppRouteSync();
  const isMobile = useIsMobile();
  const { aberto: tecladoAberto } = useVirtualKeyboard();
  const tModules = useCopy().shell.modules;

  // Uma tela de "minhas tarefas" por plataforma, nao duas.
  //
  // HojeModule e MyTasksModule respondem a mesma pergunta, e ter os dois no
  // celular e exatamente o caminho duplicado que este redesenho existe para
  // eliminar. No telefone vale o Hoje (agrupado por urgencia, com swipe); no
  // desktop segue o antigo, que tem a visao por projeto que la faz sentido.
  useEffect(() => {
    if (isMobile && currentModule === 'mytasks') changeModule('hoje');
  }, [isMobile, currentModule, changeModule]);


  // Nenhum destino e ponte: os tres sao telas de verdade.

  // Tratar checkout success/canceled do Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    if (!checkoutStatus) return;

    // Limpar URL
    window.history.replaceState({}, '', window.location.pathname);

    if (checkoutStatus === 'success') {
      showToast('Assinatura ativada com sucesso!', 'success');
      refreshSubscription();
      // Retry: webhook pode demorar
      setTimeout(() => refreshSubscription(), 3000);
      // Reforço client-side do purchase (disparado quando a subscription chegar
      // ativa no efeito abaixo). A fonte autoritativa é o webhook (server-side).
      setCheckoutJustSucceeded(true);
    } else if (checkoutStatus === 'canceled') {
      showToast('Checkout cancelado. Você pode tentar novamente quando quiser.', 'info');
      // Abandono de checkout: recupera o plano em aberto gravado no begin_checkout.
      try {
        const last = localStorage.getItem('meugantt_last_checkout');
        if (last) {
          const { plan, billing_cycle } = JSON.parse(last);
          analytics.trackCheckoutAbandoned(plan || 'unknown', billing_cycle || 'unknown');
        }
      } catch { /* ignore */ }
    }
    // Encerrou o fluxo de checkout (sucesso ou cancelamento): limpa o rascunho.
    try { localStorage.removeItem('meugantt_last_checkout'); } catch { /* ignore */ }
  }, [refreshSubscription]);

  // Reforço client-side do purchase: só para compra imediata (status 'active').
  // Em trial, o purchase é disparado pelo servidor no 1o pagamento real. Usa o
  // mesmo transaction_id (stripe_subscription_id) para o GA4 deduplicar, e um
  // flag em localStorage para não redisparar no reload.
  useEffect(() => {
    if (!checkoutJustSucceeded || !subscription) return;
    if (subscription.status !== 'active') return;
    const txId = subscription.stripe_subscription_id;
    if (!txId || !subscription.plan) return;

    const flagKey = `meugantt_purchase_tracked_${txId}`;
    try {
      if (localStorage.getItem(flagKey)) { setCheckoutJustSucceeded(false); return; }
    } catch { /* localStorage indisponível */ }

    const cents = subscription.billing_cycle === 'yearly'
      ? subscription.plan.price_yearly
      : subscription.plan.price_monthly;
    void analytics.trackPurchase((cents || 0) / 100, 'BRL', txId, subscription.plan.name, user?.email);

    try { localStorage.setItem(flagKey, '1'); } catch { /* localStorage indisponível */ }
    setCheckoutJustSucceeded(false);
  }, [checkoutJustSucceeded, subscription, user?.email]);

  // Global keyboard shortcuts (Ctrl+K, Ctrl+1-4 module nav, ? for shortcuts panel)
  useGlobalKeyboardShortcuts({
    onOpenSearch: () => setShowSearch(true),
    onOpenShortcuts: () => setShowShortcuts(true),
  });

  // Onboarding de ativação: o primeiro projeto nunca cai num Gantt vazio.
  // O welcome pergunta o tipo, a galeria de modelos abre pré-filtrada e o usuário escolhe (1 clique).
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [newProjectModal, setNewProjectModal] = useState<{ open: boolean; templateKey?: string }>({ open: false });

  // Primeiro acesso sem projetos: abre o welcome de intenção.
  useEffect(() => {
    if (loading) return;
    if (projects.length > 0) return;
    try {
      if (localStorage.getItem('meugantt_welcome_seen')) return;
    } catch { /* localStorage indisponível */ }
    setWelcomeOpen(true);
  }, [loading, projects.length]);

  // CTA "Conhecer o Professional" do teaser do tour abre o PricingPage.
  const [showPricing, setShowPricing] = useState(false);
  useEffect(() => {
    const open = () => setShowPricing(true);
    window.addEventListener('meugantt:open-pricing', open);
    return () => window.removeEventListener('meugantt:open-pricing', open);
  }, []);

  // Deep link ?upgrade=1: usado pelos e-mails de lifecycle para abrir o pricing
  // direto. Remove só o parâmetro upgrade da URL e preserva os utm_* para o GA4.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') !== '1') return;
    params.delete('upgrade');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    setShowPricing(true);
  }, []);

  // Deep link /settings?tab=<aba>: o rodape de TODO e-mail de notificacao leva
  // para as preferencias, e nenhum lugar do app lia o parametro. "Gerenciar
  // preferencias" pousava no Gantt do projeto padrao e a pessoa tinha que achar
  // a aba sozinha, ou desistir e marcar como spam, que e o desfecho caro para o
  // dominio de envio.
  //
  // O CAMINHO importa mais do que a query: `/settings` e rota de app, entao
  // sessao expirada cai no login com o destino preservado (ver o `redirectTo`
  // mais abaixo). A primeira versao disto usava `/?settings=notifications`, e a
  // raiz NAO e rota de app: quem clicasse sem sessao ia parar na landing e o
  // destino morria ali.
  //
  // Mesma cirurgia do ?upgrade=1: consome o parametro, devolve a URL limpa e
  // preserva os utm_* para o GA4.
  const [settingsTab, setSettingsTab] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const aba = params.get('tab');
    if (!aba) return;
    params.delete('tab');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    setSettingsTab(aba);
    // O caminho ja leva a Configuracoes pelo `useAppRouteSync`; isto cobre quem
    // chegar com o parametro em outro endereco.
    changeModule('settings');
  }, [changeModule]);

  // A aba pedida vale para a visita que veio do link, nao para sempre: sem
  // isto, abrir Configuracoes pelo menu tres telas depois ainda cairia em
  // Notificacoes, sem nada na tela explicando por que.
  //
  // O ref nao e enfeite. Os dois efeitos rodam no MESMO commit de montagem, e
  // ali `currentModule` ainda e o modulo anterior (o `changeModule` acima so
  // aparece no proximo render): sem a trava, este efeito zerava a aba que o
  // link acabara de pedir e o e2e caia em Preferencias. So limpa depois de ter
  // realmente passado por Configuracoes.
  const passouPorSettings = useRef(false);
  useEffect(() => {
    if (currentModule === 'settings') {
      passouPorSettings.current = true;
      return;
    }
    if (passouPorSettings.current) setSettingsTab(null);
  }, [currentModule]);

  const handleCreateFirstProject = () => {
    setNewProjectModal({ open: true });
  };

  const handleWelcomeStart = (templateKey: string) => {
    try { localStorage.setItem('meugantt_welcome_seen', '1'); } catch { /* ignore */ }
    setWelcomeOpen(false);
    setNewProjectModal({ open: true, templateKey });
  };

  const handleWelcomeClose = () => {
    try { localStorage.setItem('meugantt_welcome_seen', '1'); } catch { /* ignore */ }
    setWelcomeOpen(false);
  };

  // Modais de onboarding, montados nos dois ramos de render (empty-state e app principal).
  const onboardingModals = (
    <>
      {newProjectModal.open && (
        <AIProjectModal
          initialStep="template"
          allowTemplatesFree
          initialTemplateKey={newProjectModal.templateKey}
          onClose={() => setNewProjectModal({ open: false })}
          onProjectCreated={() => setNewProjectModal({ open: false })}
        />
      )}
      {welcomeOpen && !isMobile && (
        <WelcomeModal onStart={handleWelcomeStart} onClose={handleWelcomeClose} />
      )}
    </>
  );

  // Render the active module
  const renderModule = () => {
    // No celular, módulo de planejamento não renderiza: diz a verdade.
    //
    // A guarda vem ANTES do switch, e não como CSS, porque `max-md:hidden`
    // continuaria montando o módulo: ele buscaria dados, assinaria realtime e
    // desenharia fora da tela para ninguém. E vem antes do `FeatureGate` de
    // propósito: oferecer upgrade de plano para destravar uma tela que a pessoa
    // não vai conseguir usar naquele aparelho seria vender o problema errado.
    if (isMobile && !MODULOS_DE_CELULAR.has(currentModule)) {
      const nome = tModules[currentModule as keyof typeof tModules] || currentModule;
      return <DesktopOnlyModule moduleName={nome} />;
    }

    switch (currentModule) {
      case 'home':
        return <HomeModule onOpenSearch={() => setShowSearch(true)} />;
      case 'portfolio':
        return <FeatureGate feature="portfolio"><PortfolioModule /></FeatureGate>;
      case 'settings':
        return <SettingsModule initialTab={settingsTab ?? undefined} />;
      case 'gantt':
        return <GanttModule />;
      case 'kanban':
        return <KanbanModule />;
      case 'team':
        // Equipe & Alocação (handoff 14/08/2026). Sem capacity_planning, o
        // componente devolve a tela clássica por dentro.
        return <TeamAllocationModule />;
      case 'schedule':
        return <FeatureGate feature="timeline"><ScheduleModule /></FeatureGate>;
      case 'calendar':
        return <CalendarModule />;
      case 'todos':
        return <FeatureGate feature="todos"><TodosModule /></FeatureGate>;
      case 'raci':
        return <FeatureGate feature="raci_matrix"><RaciModule /></FeatureGate>;
      case 'eap':
        return <FeatureGate feature="eap_wbs"><EapModule /></FeatureGate>;
      case 'risks':
        return <FeatureGate feature="risks"><RisksModule /></FeatureGate>;
      case 'changes':
        return <FeatureGate feature="changes"><ChangesModule /></FeatureGate>;
      case 'costs':
        return <FeatureGate feature="costs"><CostsModule /></FeatureGate>;
      case 'wiki':
        return <WikiModule />;
      case 'hoje':
        return <HojeModule />;
      case 'mytasks':
        return <MyTasksModule />;
      default:
        return <GanttModule />;
    }
  };

  // Error state
  if (error) {
    return (
      <div className="w-screen h-screen max-md:h-[calc(100dvh_-_var(--altura-teclado,0px))] flex overflow-hidden bg-[var(--color-bg)]">
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* `h-full` e não `h-screen`: o pai já mede a tela (com `dvh` no
              celular), e um segundo `h-screen` aqui dentro ignorava esse ajuste
              e empurrava o botão de tentar de novo para debaixo da barra de
              endereço do telefone. Justamente na tela de erro, onde a única
              ação disponível é aquele botão. */}
          <div className="flex flex-col items-center justify-center h-full p-5 text-center bg-[var(--color-bg)] text-[var(--color-text)]">
            <h2 className="text-[var(--color-danger)] mb-4">{tShell.connectionErrorTitle}</h2>
            <p className="mb-3 max-w-[500px]">{error}</p>
            <p className="text-sm text-[var(--color-text-muted)] mb-5">
              {tShell.connectionErrorHint}
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => window.location.reload()}
              className="bg-[var(--color-info)] hover:bg-[var(--color-info-dark)]"
            >
              {tShell.connectionErrorAction}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && projects.length === 0) {
    return (
      <div className="w-screen h-screen max-md:h-[calc(100dvh_-_var(--altura-teclado,0px))] flex overflow-hidden bg-[var(--color-bg)]">
        <Sidebar onOpenSearch={() => setShowSearch(true)} mobileSidebarOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-center w-full h-full text-[var(--color-text-muted)]">
            <div data-testid="app-loading-spinner" className="w-10 h-10 border-3 border-[var(--color-border)] border-t-[var(--color-primary)] rounded-full animate-spin"></div>
          </div>
        </div>
        <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
      </div>
    );
  }

  // Empty state - no projects (mas permite acesso a Home e Settings)
  if (!project && !loading && currentModule !== 'home' && currentModule !== 'settings' && currentModule !== 'portfolio' && currentModule !== 'wiki' && currentModule !== 'mytasks') {
    return (
      <div className="w-screen h-screen max-md:h-[calc(100dvh_-_var(--altura-teclado,0px))] flex overflow-hidden bg-[var(--color-bg)]">
        <Sidebar onOpenSearch={() => setShowSearch(true)} mobileSidebarOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex flex-col items-center justify-center w-full h-full p-10 text-center">
            <FolderKanban size={64} className="w-20 h-20 mb-4 text-[var(--color-text-muted)]" />
            <h2 className="text-lg font-semibold text-[var(--color-text)] mb-2">{tShell.noProjectTitle}</h2>
            <p className="text-sm text-[var(--color-text-secondary)] mb-6 max-w-[400px]">
              {tShell.noProjectDescription}
            </p>
            <Button variant="primary" size="lg" icon={Plus} onClick={handleCreateFirstProject}>
              {tShell.noProjectAction}
            </Button>
          </div>
        </div>
        {showSettings && (
          <ProjectSettingsModal onClose={() => setShowSettings(false)} />
        )}
        <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
        {onboardingModals}
      </div>
    );
  }

  return (
    <div className="w-screen h-screen max-md:h-[calc(100dvh_-_var(--altura-teclado,0px))] flex overflow-hidden bg-[var(--color-bg)]">
      {/* Skip to content link for keyboard/screen reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-[var(--color-primary)] focus:text-white focus:rounded-[var(--radius-md)] focus:text-sm focus:font-medium"
      >
        {tShell.skipToContent}
      </a>

      {/* Live region for dynamic announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" id="announcer" />

      <Sidebar onOpenSearch={() => setShowSearch(true)} mobileSidebarOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)} />

      {/* safe-area: com viewport-fit=cover no index.html o app desenha ate a
          borda da tela, entao sem estes insets o cabecalho do modulo fica
          debaixo da Dynamic Island e o rodape atras da barra de gestos. */}
      <main id="main-content" className="flex-1 flex flex-col overflow-hidden max-md:pt-[env(safe-area-inset-top)]" role="main">
        {/* Hambúrguer só existe no desktop estreito, não no celular: lá a
            navegação é a barra inferior, e manter dois caminhos para a mesma
            coisa é o que fazia a gaveta continuar sendo a cara do app. */}
        {!isMobile && <button
          // `position: fixed` usa o viewport como containing block e IGNORA o
          // padding de safe-area do <main>: quem é fixed tem que carregar o
          // próprio inset, senão este botão cai embaixo da Dynamic Island.
          //
          // z-20 e não z-[98]: o modal é z-50, e com 98 este botão flutuava por
          // CIMA de todo modal aberto no celular, cortando o cabeçalho (o "K" de
          // "Kanban" no breadcrumb do card, o título do painel de atalhos) e,
          // pior, abrindo a gaveta lateral por cima do modal quando tocado.
          // Ele só precisa ficar acima do ModuleTabBar, que é z-10; a gaveta
          // (z-99/z-100) continua cobrindo ele normalmente.
          className="hidden max-md:flex fixed top-[max(0.75rem,calc(env(safe-area-inset-top)+0.5rem))] left-3 z-20 items-center justify-center w-10 h-10 rounded-[var(--radius-md)] bg-[var(--color-bg)] border border-[var(--color-border)] shadow-md text-[var(--color-text)]"
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileSidebarOpen(true)}
          aria-label={tShell.openMenu}
        >
          <Menu size={20} />
        </button>}

        {/* A barra de abas some junto com o módulo no celular: mostrar onze
            abas de planejamento empilhadas sobre o card "funciona melhor no
            computador" desmentiria o próprio recado. */}
        {!isMobile && currentModule !== 'home' && currentModule !== 'settings' && currentModule !== 'portfolio' && currentModule !== 'wiki' && (
          <ModuleTabBar onOpenSettings={() => setShowSettings(true)} />
        )}
        {/* Caixa própria para o módulo.
            Sem ela, um módulo com `h-full` na raiz (Kanban, Gantt e a maioria)
            ocupa 100% da altura do <main> e empurra a barra inferior para fora
            do `overflow-hidden`: o menu simplesmente some ao entrar na tela.
            Com a caixa, o `h-full` mede contra ela e a barra fica de fora. */}
        <div className="flex-1 min-h-0 flex flex-col">
        {telaDoShell === 'projetos' ? <ProjetosScreen onSair={() => setTelaDoShell(null)} />
         : telaDoShell === 'notificacoes' ? <AvisosScreen onSair={() => setTelaDoShell(null)} />
         : telaDoShell === 'ajustes' ? <AjustesScreen onSair={() => setTelaDoShell(null)} /> : (
        <ModuleErrorBoundary key={currentModule}>
          {/* Todo módulo é lazy: este Suspense é o que cobre a troca de tela. */}
          <Suspense fallback={<ModuleLoadingScreen />}>
            {renderModule()}
          </Suspense>
        </ModuleErrorBoundary>
        )}
        </div>

        {/* A barra fica DENTRO do <main> e fora do fluxo de rolagem: fixa no
            rodapé ela cobriria o último item de toda lista, e o usuário nunca
            alcançaria a tarefa mais antiga sem fechar o app.

            Some enquanto o teclado está aberto. Com o shell já descontando
            `--altura-teclado`, mantê-la faria a navegação do app boiar logo
            acima do teclado, que é o sinal mais óbvio de web mal adaptada; e
            ela rouba 64px justamente do momento em que a tela está menor e a
            pessoa precisa ver o que digita. Ninguém troca de aba no meio de
            uma frase. */}
        {!tecladoAberto && (
          <MobileTabBar telaDoShell={telaDoShell} onTelaDoShell={setTelaDoShell} />
        )}
      </main>

      {showSettings && (
        <ProjectSettingsModal onClose={() => setShowSettings(false)} />
      )}

      <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
      {/* Onboarding e coisa de desktop.
       *
       * Os oito passos do tour apontam para task-name-cell, gantt-chart,
       * add-task, critical-path-toggle, responsavel-filter, module-tabs,
       * baseline-save e premium-teaser. NENHUM existe no celular: a barra de
       * abas saiu, e o Gantt nem renderiza la (mostra "funciona melhor no
       * computador"). O tour nao ficava parcialmente errado, ficava inteiro.
       *
       * O WelcomeModal sai pela mesma coerencia: ele leva a galeria de modelos
       * para criar o primeiro projeto, e o estado vazio de Projetos ja diz que
       * criar projeto se faz no computador. Nao marcamos o flag de "visto",
       * entao quem entrar pelo computador depois recebe o onboarding inteiro. */}
      {!isMobile && <OnboardingTour />}
      <GettingStartedChecklist />
      <KeyboardShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
      <TrialExpiredModal />
      <CompleteWhatsappModal />
      {onboardingModals}
      {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}
      {/* Convite de instalacao so para quem ja entrou: na landing, pedir
          para instalar antes de a pessoa ter conta e pedir compromisso
          antes da entrega. */}
      <PwaPrompts />
    </div>
  );
};

const AppContent = () => {
  const { locale } = useLocale();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Mostrar loading enquanto verifica autenticação
  if (authLoading) {
    return (
      <div className="w-screen h-screen max-md:h-[calc(100dvh_-_var(--altura-teclado,0px))] flex overflow-hidden bg-[var(--color-bg)]">
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-center w-full h-full text-[var(--color-text-muted)]">
            <div data-testid="app-loading-spinner" className="w-10 h-10 border-3 border-[var(--color-border)] border-t-[var(--color-primary)] rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  // Se não autenticado, mostrar landing page.
  //
  // Menos quando o endereço PEDE uma tela do app: link de tarefa colado no
  // WhatsApp, clique em notificação, atalho do PWA. Quem chega por um desses
  // não é visitante para converter, é gente que já sabe o que veio buscar, e
  // jogar essa pessoa na landing perde o destino no caminho. O `redirectTo`
  // leva o endereço inteiro (com o `?task=`) para o outro lado do login.
  if (!isAuthenticated) {
    if (isAppPath(window.location.pathname)) {
      return <LoginScreen redirectTo={window.location.pathname + window.location.search} />;
    }
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <LandingPage />
      </Suspense>
    );
  }

  // Usuário autenticado - carregar app completo.
  //
  // key={locale}: trocar idioma REMONTA esta subárvore. Sem isso, sete coisas
  // continuariam mostrando texto do idioma anterior: estado inicializado a
  // partir de constante traduzida (as colunas do Gantt são o caso concreto),
  // useMemo sem o copy nas dependências, key={label} em listas, séries do
  // Recharts, título do documento, toasts capturados em closure e ordenação de
  // nomes. Remontar custa um spinner numa ação que se faz uma vez; caçar 160
  // useMemo no escuro não tem volta. Tirar o key depois é reversível.
  return (
    <SubscriptionProvider key={locale}>
      <OrganizationProvider>
        <UIStateProvider>
          <UndoRedoProvider>
            <ProjectFolderProvider>
              <ProjectProvider>
                <OnboardingProvider>
                  <ConfirmProvider>
                    <AuthenticatedApp />
                  </ConfirmProvider>
                </OnboardingProvider>
              </ProjectProvider>
            </ProjectFolderProvider>
          </UndoRedoProvider>
        </UIStateProvider>
      </OrganizationProvider>
    </SubscriptionProvider>
  );
};

// Lazy load das páginas legais e do blog
const PrivacyPolicyPage = lazyModule(() => import('./components/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazyModule(() => import('./components/legal/TermsOfServicePage'));
const BlogListPage = lazyModule(() => import('./components/blog/BlogListPage'));
const BlogPostPage = lazyModule(() => import('./components/blog/BlogPostPage'));
const McpConnectorPage = lazyModule(() => import('./components/marketing/McpConnectorPage'));
const ClaudeChatMockup = lazyModule(() => import('./components/landing/dev/ClaudeChatMockup'));

const AppRoutes = () => {
  // O prefixo de idioma (/en, /es) sai aqui, uma vez. Todo o roteamento abaixo
  // continua casando os mesmos caminhos sem saber que idioma existe.
  //
  // O blog é a exceção que precisa do `locale` de volta: lá o idioma faz parte
  // do endereço do conteúdo (cada post tem um slug por idioma), então a rota
  // entrega o idioma DA URL. Usar o `useLocale()` no lugar disso faria
  // `/blog/<slug-pt>` redirecionar para quem tivesse inglês salvo na sessão.
  const { path, locale: localeDaUrl } = splitLocale(window.location.pathname);

  // Verificar se é a rota de admin
  const isAdmin = path.match(/^\/admin/);
  if (isAdmin) {
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <AdminApp />
      </Suspense>
    );
  }

  // Verificar se é a rota de reset-password
  const isResetPassword = path.match(/^\/reset-password/);
  if (isResetPassword) {
    return (
      <AuthProvider>
        <ResetPasswordScreen />
      </AuthProvider>
    );
  }

  // Verificar se é uma URL de compartilhamento público
  const shareMatch = path.match(/^\/share\/([a-zA-Z0-9-]+)$/);
  if (shareMatch) {
    const shareToken = shareMatch[1];
    return <SharedProjectView shareToken={shareToken} />;
  }

  // Verificar se é a rota de convite de plano
  const inviteMatch = path.match(/^\/invite\/([a-zA-Z0-9-]+)$/);
  if (inviteMatch) {
    const inviteToken = inviteMatch[1];
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <InviteAcceptPage token={inviteToken} />
      </Suspense>
    );
  }

  // Verificar se é a rota de convite de organização
  const orgInviteMatch = path.match(/^\/org-invite\/([a-zA-Z0-9]+)$/);
  if (orgInviteMatch) {
    const orgInviteToken = orgInviteMatch[1];
    return (
      <AuthProvider>
        <Suspense fallback={<LazyLoadingScreen />}>
          <OrgInviteAcceptPage token={orgInviteToken} />
        </Suspense>
      </AuthProvider>
    );
  }

  // Verificar se é a rota de convite de projeto
  const projectInviteMatch = path.match(/^\/project-invite\/([a-zA-Z0-9]+)$/);
  if (projectInviteMatch) {
    const projectInviteToken = projectInviteMatch[1];
    return (
      <AuthProvider>
        <Suspense fallback={<LazyLoadingScreen />}>
          <ProjectInviteAcceptPage token={projectInviteToken} />
        </Suspense>
      </AuthProvider>
    );
  }

  // Blog: lista de posts
  const isBlogList = path === '/blog' || path === '/blog/';
  if (isBlogList) {
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <BlogListPage locale={localeDaUrl} />
      </Suspense>
    );
  }

  // Blog: post individual por slug.
  //
  // O slug é resolvido DENTRO da página, contra a cópia do idioma da URL, e não
  // aqui: quem sabe se `/en/blog/<slug-pt>` é post, redirecionamento ou 404 é o
  // `resolveBlogRoute`, que precisa dos três idiomas carregados.
  const blogPostMatch = path.match(/^\/blog\/([a-z0-9-]+)\/?$/);
  if (blogPostMatch) {
    const blogSlug = blogPostMatch[1];
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <BlogPostPage slug={blogSlug} locale={localeDaUrl} />
      </Suspense>
    );
  }

  // Página do conector MCP para o Claude (público)
  const isMcpConnector = path.match(/^\/integracoes\/claude\/?$/);
  if (isMcpConnector) {
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <McpConnectorPage />
      </Suspense>
    );
  }

  // Mockup do chat Claude — dev-only, usado para capturar screenshot da landing (CLAUDE-MCP.jpg)
  if (import.meta.env.DEV && path === '/dev/claude-mockup') {
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <ClaudeChatMockup />
      </Suspense>
    );
  }

  // Verificar se é a página de Política de Privacidade
  const isPrivacyPolicy = path.match(/^\/privacidade$/);
  if (isPrivacyPolicy) {
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <PrivacyPolicyPage />
      </Suspense>
    );
  }

  // Verificar se é a página de Termos de Uso
  const isTermsOfService = path.match(/^\/termos$/);
  if (isTermsOfService) {
    return (
      <Suspense fallback={<LazyLoadingScreen />}>
        <TermsOfServicePage />
      </Suspense>
    );
  }

  // Verificar se é a rota de login
  const isLogin = path.match(/^\/login/);
  if (isLogin) {
    return (
      <AuthProvider>
        <LoginScreen />
      </AuthProvider>
    );
  }

  // Verificar se é a rota de consent OAuth (Claude/MCP connector)
  const isOAuthConsent = path === '/oauth/consent';
  if (isOAuthConsent) {
    return (
      <AuthProvider>
        <Suspense fallback={<LazyLoadingScreen />}>
          <OAuthConsentPage />
        </Suspense>
      </AuthProvider>
    );
  }

  // Renderização normal do app (com autenticação)
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

const App = () => (
  // LocaleProvider no topo, junto do tema: antes ele envolvia só a landing, e
  // por isso quem clicava em "Entrar" numa landing em inglês caía num login em
  // português. Agora cobre login, app autenticado, toasts e cookie banner.
  <LocaleProvider>
    <ThemeProvider>
      <TooltipProvider>
        <AppRoutes />
        <CookieConsent />
        <Toaster />
      </TooltipProvider>
    </ThemeProvider>
  </LocaleProvider>
);

export default App;
